import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet, Upload } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { ResponsiveDialog } from "@/components/common/ResponsiveDialog";
import { Chip } from "@/components/common/StatusBadge";
import { api, errorMessage } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { CATALOG_CATEGORY_SHORT, unitShort } from "@/lib/i18n";
import { IMPORT_COLUMNS, buildImportRows, decimalNb, downloadText, toCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

const TEMPLATE_ROWS = [
  ["M-101", "Veigrus 0–11 mm", "masse", "t", "200,00", "25", "1,6", "Knust grus til vegdekke", "ja"],
  ["T-101", "Transporttime med sjåfør", "teneste", "time", "1000,00", "25", "", "", "ja"],
  ["R-103", "Tippbil med sjåfør", "ressurs", "time", "1100,00", "25", "", "Sats for bil inkl. sjåfør", "ja"],
  ["V-106", "Ferjebillett, vidarefakturert", "vare", "stk", "420,00", "0", "", "Takst B2 – justerast mot billett", "ja"],
];

export const downloadCatalogTemplate = () => downloadText("prisliste-mal.csv", toCsv(IMPORT_COLUMNS.map((c) => c.label), TEMPLATE_ROWS));

export const downloadCatalogCsv = (catalog) => {
  const rows = [...catalog]
    .sort((a, b) => (a.category || "").localeCompare(b.category || "") || (a.sku || "").localeCompare(b.sku || "", "nb") || a.name.localeCompare(b.name, "nb"))
    .map((c) => [c.sku || "", c.name, c.category, c.unit, decimalNb(c.unit_price), decimalNb(c.vat_rate), decimalNb(c.density_t_per_m3), c.description || "", c.active === false ? "nei" : "ja"]);
  const date = new Date().toISOString().slice(0, 10);
  downloadText(`prisliste-${date}.csv`, toCsv(IMPORT_COLUMNS.map((c) => c.label), rows));
};

const ACTION_TONE = { ny: "success", oppdater: "info", uendra: "neutral" };
const ACTION_LABEL = { ny: "Ny", oppdater: "Oppdaterast", uendra: "Uendra" };
const CHANGE_LABEL = { pris: "pris", varenr: "varenr", kategori: "kategori", eining: "eining", mva: "mva", densitet: "densitet", skildring: "skildring", aktiv: "aktiv" };

/**
 * Import av prisliste i tre steg: 1) lim inn / last opp CSV, 2) førehandsvising (dry run på server), 3) stadfest.
 * Ingenting blir lagra før brukaren trykkjer «Importer». Rader med feil blir aldri lagra.
 */
export const CatalogImportDialog = ({ open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null); // { rows, columns, error }
  const [preview, setPreview] = useState(null); // svar frå dry run
  const [result, setResult] = useState(null); // svar frå ekte import

  useEffect(() => {
    if (!open) return;
    setText("");
    setFileName("");
    setParsed(null);
    setPreview(null);
    setResult(null);
  }, [open]);

  const dryRun = useMutation({
    mutationFn: async (rows) => (await api.post("/catalog/import", { rows, dry_run: true })).data,
    onSuccess: (data) => setPreview(data),
  });
  const commit = useMutation({
    mutationFn: async (rows) => (await api.post("/catalog/import", { rows, dry_run: false })).data,
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Prisliste importert", { description: `${data.created} nye · ${data.updated} oppdaterte${data.unchanged ? ` · ${data.unchanged} uendra` : ""}${data.errors.length ? ` · ${data.errors.length} hoppa over` : ""}` });
    },
  });

  const onFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    const buf = await file.arrayBuffer();
    let decoded = new TextDecoder("utf-8", { fatal: false }).decode(buf);
    // Excel på Windows lagrar ofte som Latin-1/CP1252 – oppdag erstatningsteikn og prøv på nytt
    if (decoded.includes("\uFFFD")) decoded = new TextDecoder("windows-1252").decode(buf);
    setText(decoded);
  };

  const analyse = () => {
    const p = buildImportRows(text);
    setParsed(p);
    if (!p.error && p.rows.length) dryRun.mutate(p.rows);
  };

  const stats = useMemo(() => {
    if (!preview) return null;
    const count = (a) => preview.preview.filter((p) => p.action === a).length;
    return { ny: count("ny"), oppdater: count("oppdater"), uendra: count("uendra"), feil: preview.errors.length };
  }, [preview]);

  const step = result ? 3 : preview ? 2 : 1;
  const canCommit = stats && stats.ny + stats.oppdater > 0 && !commit.isPending;
  const mappedColumns = parsed?.columns?.filter(Boolean) || [];

  const footer = step === 1 ? (
    <>
      <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => onOpenChange(false)} data-testid="katalog-import-avbryt">Avbryt</Button>
      <Button type="button" className="h-11 md:h-10" disabled={!text.trim() || dryRun.isPending} onClick={analyse} data-testid="katalog-import-analyser">{dryRun.isPending ? "Kontrollerer …" : "Kontroller prisliste"}</Button>
    </>
  ) : step === 2 ? (
    <>
      <Button type="button" variant="outline" className="h-11 bg-card md:h-10" onClick={() => setPreview(null)} data-testid="katalog-import-tilbake"><ArrowLeft className="mr-2 h-4 w-4" /> Tilbake</Button>
      <Button type="button" className="h-11 md:h-10" disabled={!canCommit} onClick={() => commit.mutate(parsed.rows)} data-testid="katalog-import-stadfest">
        {commit.isPending ? "Importerer …" : stats ? `Importer ${stats.ny} nye og oppdater ${stats.oppdater}` : "Importer"}
      </Button>
    </>
  ) : (
    <Button type="button" className="h-11 md:h-10" onClick={() => onOpenChange(false)} data-testid="katalog-import-lukk">Lukk</Button>
  );

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Importer prisliste"
      description={step === 1 ? "CSV frå Excel eller rekneskap. Matchar på varenr, elles på namn. Ingenting blir lagra før du stadfestar." : step === 2 ? "Kontroller endringane før du importerer. Rader med feil blir hoppa over." : "Importen er fullført."}
      testId="katalog-import-dialog"
      wide
      footer={footer}
    >
      {step === 1 ? (
        <div className="space-y-4" data-testid="katalog-import-steg-1">
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="focus-ring flex min-h-[72px] items-center gap-3 rounded-lg border border-dashed border-border bg-background px-4 py-3 text-left hover:border-foreground/40 hover:bg-muted"
              data-testid="katalog-import-velg-fil"
            >
              <Upload className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{fileName || "Vel CSV-fil"}</span>
                <span className="block truncate text-xs text-muted-foreground">{fileName ? "Klikk for å byte fil" : "Semikolon, komma eller tab. UTF-8 eller Excel-format."}</span>
              </span>
            </button>
            <input ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain" className="sr-only" onChange={(e) => onFile(e.target.files?.[0])} data-testid="katalog-import-fil" />
            <button
              type="button"
              onClick={downloadCatalogTemplate}
              className="focus-ring flex min-h-[72px] items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 text-left hover:bg-muted"
              data-testid="katalog-import-last-ned-mal"
            >
              <FileSpreadsheet className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-sm font-medium">Last ned mal</span>
                <span className="block text-xs text-muted-foreground">Kolonnar: {IMPORT_COLUMNS.map((c) => c.label).join(", ")}</span>
              </span>
            </button>
          </div>
          <div className="space-y-2">
            <label htmlFor="katalog-import-tekst" className="text-sm font-medium">… eller lim inn frå rekneark</label>
            <Textarea
              id="katalog-import-tekst"
              value={text}
              onChange={(e) => { setText(e.target.value); setParsed(null); }}
              rows={7}
              placeholder={"Varenr;Namn;Kategori;Eining;Pris eks. mva;MVA %\nM-101;Veigrus 0–11 mm;masse;t;200,00;25\nT-101;Transporttime med sjåfør;teneste;time;1000,00;25"}
              className="num min-h-[140px] bg-card text-xs leading-5"
              spellCheck={false}
              data-testid="katalog-import-tekst"
            />
            <p className="text-xs text-muted-foreground">Første rad kan vere overskrifter (vi kjenner att nynorsk, bokmål og engelsk). Tom pris = pris manglar og blokkerer godkjenning. Kategori: masse, vare, teneste eller ressurs.</p>
          </div>
          {parsed?.error ? <Alert variant="destructive" className="bg-card" data-testid="katalog-import-feil"><AlertTriangle className="h-4 w-4" /><AlertDescription>{parsed.error}</AlertDescription></Alert> : null}
          {dryRun.isError ? <Alert variant="destructive" className="bg-card" data-testid="katalog-import-feil"><AlertTriangle className="h-4 w-4" /><AlertDescription>{errorMessage(dryRun.error)}</AlertDescription></Alert> : null}
        </div>
      ) : null}

      {step === 2 && preview ? (
        <div className="space-y-4" data-testid="katalog-import-steg-2">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="katalog-import-oppsummering">
            {[
              ["Nye", stats.ny, "success", "katalog-import-tal-ny"],
              ["Oppdaterast", stats.oppdater, "info", "katalog-import-tal-oppdater"],
              ["Uendra", stats.uendra, "neutral", "katalog-import-tal-uendra"],
              ["Feil (hoppa over)", stats.feil, stats.feil ? "destructive" : "neutral", "katalog-import-tal-feil"],
            ].map(([label, n, tone, tid]) => (
              <div key={label} className="rounded-lg border border-border bg-background px-3 py-2" data-testid={tid}>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className={cn("num text-xl font-semibold leading-7", tone === "destructive" && "text-destructive", tone === "success" && "text-success", tone === "info" && "text-info")}>{n}</p>
              </div>
            ))}
          </div>
          {mappedColumns.length ? <p className="text-xs text-muted-foreground">Kolonnar funne: {mappedColumns.map((k) => IMPORT_COLUMNS.find((c) => c.key === k)?.label || k).join(", ")}{parsed.header ? "" : " (ingen overskriftsrad – standardrekkjefølgje brukt)"}</p> : null}

          {preview.errors.length ? (
            <div className="rounded-lg border border-destructive/40 bg-tint-destructive/40" data-testid="katalog-import-feilliste">
              <p className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-destructive"><AlertTriangle className="h-4 w-4" /> {preview.errors.length} rad{preview.errors.length === 1 ? "" : "er"} med feil blir ikkje importert</p>
              <ul className="divide-y divide-border border-t border-border">
                {preview.errors.map((e) => (
                  <li key={`${e.row}-${e.error}`} className="grid grid-cols-[48px_1fr] gap-2 px-3 py-2 text-sm" data-testid="katalog-import-feilrad">
                    <span className="num text-muted-foreground">Rad {e.row}</span>
                    <span className="min-w-0"><span className="font-medium">{e.name || "–"}</span><span className="block text-muted-foreground">{e.error}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {preview.preview.length ? (
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <Table data-testid="katalog-import-forhandsvising">
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="w-10">Rad</TableHead>
                    <TableHead>Vare</TableHead>
                    <TableHead className="hidden sm:table-cell">Kategori</TableHead>
                    <TableHead className="text-right">Pris</TableHead>
                    <TableHead>Handling</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.preview.map((p) => (
                    <TableRow key={p.row} className={cn(p.action === "uendra" && "text-muted-foreground")} data-testid="katalog-import-rad">
                      <TableCell className="num text-xs text-muted-foreground">{p.row}</TableCell>
                      <TableCell>
                        <span className="block font-medium">{p.name}</span>
                        <span className="num block text-xs text-muted-foreground">{p.sku || "utan varenr"} · per {unitShort(p.unit)}</span>
                      </TableCell>
                      <TableCell className="hidden text-sm sm:table-cell">{CATALOG_CATEGORY_SHORT[p.category] || p.category}</TableCell>
                      <TableCell className="num text-right">
                        <span className={cn("block", p.unit_price === null && "font-medium text-destructive")}>{p.unit_price === null ? "Manglar" : fmtMoney(p.unit_price)}</span>
                        {p.action === "oppdater" && p.previous_price !== p.unit_price ? <span className="block text-xs text-muted-foreground line-through">{p.previous_price === null ? "manglar" : fmtMoney(p.previous_price)}</span> : null}
                      </TableCell>
                      <TableCell>
                        <Chip tone={ACTION_TONE[p.action]} outline={p.action === "uendra"}>{ACTION_LABEL[p.action]}</Chip>
                        {p.changes?.length ? <span className="mt-1 block text-[11px] text-muted-foreground">{p.changes.map((c) => CHANGE_LABEL[c] || c).join(", ")}</span> : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
          {commit.isError ? <Alert variant="destructive" className="bg-card" data-testid="katalog-import-feil"><AlertTriangle className="h-4 w-4" /><AlertDescription>{errorMessage(commit.error)}</AlertDescription></Alert> : null}
        </div>
      ) : null}

      {step === 3 && result ? (
        <div className="space-y-4" data-testid="katalog-import-steg-3">
          <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden="true" />
            <div className="space-y-1">
              <p className="text-sm font-medium" data-testid="katalog-import-resultat">{result.created} nye varer lagt til · {result.updated} oppdaterte{result.unchanged ? ` · ${result.unchanged} uendra` : ""}</p>
              <p className="text-xs text-muted-foreground">Nye prisar gjeld berre nye kladdar og kalkylar. Godkjende fakturagrunnlag er uendra.</p>
              {result.errors.length ? <p className="text-xs text-destructive">{result.errors.length} rad{result.errors.length === 1 ? "" : "er"} vart hoppa over på grunn av feil.</p> : null}
            </div>
          </div>
          <Button type="button" variant="outline" className="h-11 w-full bg-card sm:w-auto md:h-10" onClick={() => { setResult(null); setPreview(null); setText(""); setFileName(""); setParsed(null); }} data-testid="katalog-import-ny"><Download className="mr-2 h-4 w-4" /> Importer ei ny fil</Button>
        </div>
      ) : null}
    </ResponsiveDialog>
  );
};
